import { useAuth } from '@clerk/clerk-react';
import { useCallback } from 'react';
import { z } from 'zod';

const JwtPayloadSchema = z.object({
  exp: z.number().int().positive().optional(),
});

type TokenCacheEntry = {
  token: string | null;
  expiresAtMs: number;
  inFlight: Promise<string | null> | null;
};

const tokenCache = new Map<string, TokenCacheEntry>();
const TOKEN_REFRESH_SKEW_MS = 15_000;
const DEFAULT_TOKEN_CACHE_MS = 30_000;
const NULL_TOKEN_CACHE_MS = 1_000;

const getCacheKey = (sessionId: string | null | undefined, template?: string) =>
  `${sessionId ?? 'anonymous'}:${template ?? 'default'}`;

const getOrCreateEntry = (key: string): TokenCacheEntry => {
  const existing = tokenCache.get(key);
  if (existing) {
    return existing;
  }
  const created: TokenCacheEntry = {
    token: null,
    expiresAtMs: 0,
    inFlight: null,
  };
  tokenCache.set(key, created);
  return created;
};

const decodeJwtExpiryMs = (token: string): number | null => {
  const parts = token.split('.');
  const payload = parts[1];
  if (!payload) {
    return null;
  }
  const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);

  if (typeof globalThis.atob !== 'function') {
    return null;
  }

  try {
    const decoded = globalThis.atob(padded);
    const parsed = JSON.parse(decoded) as unknown;
    const validated = JwtPayloadSchema.safeParse(parsed);
    if (!validated.success || typeof validated.data.exp !== 'number') {
      return null;
    }
    return validated.data.exp * 1000;
  } catch {
    return null;
  }
};

type UseClerkTokenResult = {
  getToken: () => Promise<string | null>;
  isLoaded: boolean;
  isSignedIn: boolean | undefined;
};

export const useClerkToken = (template?: string): UseClerkTokenResult => {
  const { getToken, isLoaded, isSignedIn, sessionId } = useAuth();

  const getCachedToken = useCallback(async () => {
    const key = getCacheKey(sessionId, template);
    const entry = getOrCreateEntry(key);
    const now = Date.now();

    if (entry.token && now < entry.expiresAtMs - TOKEN_REFRESH_SKEW_MS) {
      return entry.token;
    }
    if (entry.inFlight) {
      return entry.inFlight;
    }

    entry.inFlight = (async () => {
      const nextToken = template ? await getToken({ template }) : await getToken();
      entry.token = nextToken ?? null;

      const fetchedAt = Date.now();
      if (!nextToken) {
        entry.expiresAtMs = fetchedAt + NULL_TOKEN_CACHE_MS;
        return null;
      }

      const tokenExpiryMs = decodeJwtExpiryMs(nextToken);
      if (tokenExpiryMs && tokenExpiryMs > fetchedAt + TOKEN_REFRESH_SKEW_MS) {
        entry.expiresAtMs = tokenExpiryMs;
      } else {
        entry.expiresAtMs = fetchedAt + DEFAULT_TOKEN_CACHE_MS;
      }
      return nextToken;
    })();

    try {
      return await entry.inFlight;
    } finally {
      entry.inFlight = null;
    }
  }, [getToken, sessionId, template]);

  return {
    getToken: getCachedToken,
    isLoaded,
    isSignedIn,
  };
};

export default useClerkToken;
